import * as React from 'react'
// libs
import { useRouter } from 'hooks/useRouter'
import { reduxForm } from 'redux-form'
import { Draggable, Droppable, DragDropContext } from 'react-beautiful-dnd'
// hooks
import useModalData from 'hooks/useShowModal'
// types
import { InjectedFormProps } from 'redux-form/lib/reduxForm'
import { IField, IFieldUpdatePositionInput } from 'types/graphql'
// view components
import MatrixModal from 'components/Modals/MatrixModal'
import AddFormDrawer from 'components/Modals/RiskFormModal'
import RiskTemplateField from './components/RiskTemplateField'
import ConfirmDeleteModal from 'components/Modals/ConfirmDeleteModal'
import FieldHistoryDrawer from 'components/Modals/FieldHistoryModal'
import FieldDetailsDrawer from 'components/Modals/FieldDetailsDrawer'
import LinkedFormViewModal from 'components/Modals/LinkedFormViewModal'
// context
import { RiskTemplateAPIContext } from 'containers/Risks/RiskTemplate/Edit/layers/API'
// constants
import { Forms, Modals } from 'constants/index'
// views
import { DroppableContainer, GlobalStyles } from './views'
// utils
import { capitalize, orderBy } from 'lodash'

const RiskTemplateFields: React.FC<InjectedFormProps> = () => {
  const router = useRouter()

  const { riskTemplate, isRiskTemplate, reorderFields } = React.useContext(RiskTemplateAPIContext)

  const {
    modalData: { action = 'Delete', entity = 'Form' }
  } = useModalData({ modalType: Modals.CONFIRM_DELETE_RISK_MODAL })

  const fields: IField[] = orderBy(
    riskTemplate.fields.filter((field) => !field.parentFieldId),
    'position'
  )

  const reorder = (list: IField[], startIdx: number, endIdx: number) => {
    const fieldsOrderInput: IFieldUpdatePositionInput[] = []

    const [removed] = list.splice(startIdx, 1)

    list.splice(endIdx, 0, removed)

    list.forEach((field, idx) => {
      if (field?.position !== idx) {
        fieldsOrderInput.push({
          id: field.id,
          position: idx
        })
      }
    })

    return fieldsOrderInput
  }

  const onDragEnd = ({ source, destination }) => {
    if (!destination) {
      return
    }

    if (source.droppableId === destination.droppableId) {
      const fieldsOrderInput = reorder(fields, source.index, destination.index)

      if (fieldsOrderInput.length) {
        reorderFields(fieldsOrderInput)
      }
    }
  }

  return (
    <DragDropContext onDragEnd={onDragEnd}>
      <Droppable droppableId='RISK_FIELDS'>
        {(provided) => (
          <DroppableContainer ref={provided.innerRef} {...provided.droppableProps}>
            {fields.map((field, idx) => (
              <Draggable
                index={idx}
                key={field.id}
                draggableId={field.id}
                isDragDisabled={!isRiskTemplate || !!router.query.archived}>
                {(provided) => (
                  <div
                    ref={provided.innerRef}
                    {...provided.draggableProps}
                    style={provided.draggableProps.style as React.CSSProperties}>
                    <RiskTemplateField dragHandleProps={provided.dragHandleProps} field={field} />
                  </div>
                )}
              </Draggable>
            ))}
            {provided.placeholder}
          </DroppableContainer>
        )}
      </Droppable>

      <MatrixModal />
      <AddFormDrawer />
      <FieldHistoryDrawer />
      <FieldDetailsDrawer />
      <LinkedFormViewModal />
      <ConfirmDeleteModal header={`${action} ${capitalize(entity)}`} modalType={Modals.CONFIRM_DELETE_RISK_MODAL}>
        {`Are you sure you want to ${action.toLowerCase()} this ${entity.toLowerCase()}?`}
      </ConfirmDeleteModal>

      <GlobalStyles />
    </DragDropContext>
  )
}

export default reduxForm({
  form: Forms.RISK_TEMPLATE_FORM,
  enableReinitialize: true
})(RiskTemplateFields)
